/** The other two tabs of Action items: what you submitted, and what has been decided.
 *
 * **Counts are the server's.** Each list is a server-paged envelope; the filter line prints "Showing
 * 50 of 212" from `total`, and the grid orders the page it holds and says nothing about the rest. A
 * tile built from the loaded rows is a count of the page, so it carries a `+` when the page is not
 * the whole list (`pageCount`).
 *
 * **A state is a word and a chip**, and an unfamiliar state is drawn as itself, in neutral, never as
 * a pass. **No rationale is shown**: the server returns none (it stays in the workspace and out of
 * the chain, rule 4), and a decision is shown as who, when and the outcome.
 *
 * **Empty and failed are different renderings** (KEEP.md 19): a failed read is the shared
 * `ReadFailed` in the table's place, with a retry, and never the empty sentence.
 */

import { CheckCircle, Hourglass } from "@phosphor-icons/react";
import { useState } from "react";

import type { ListedRequest, ListedRequests } from "../api/approvals";
import { useDecided, useSubmittedByMe } from "../api/approvals";
import { useReader } from "../api/profile";
import { ReadFailed } from "../components/Loaded";
import { EmptyState, EntityLink, FilterLine, GridTable, HowDrawer, StatusChip, TablePage, Tile, TileStrip } from "../components/ui";
import type { ChipTone, FilterState, GridColumn } from "../components/ui";
import { EMPTY_FILTERS } from "../components/ui";
import { instantDate } from "../lib/format";
import { useGridSort } from "./gridSort";
import { kindLabel } from "./kinds";
import { PersonRef } from "./PersonRef";

const STATE: Record<string, { word: string; tone: ChipTone }> = {
  pending: { word: "In review", tone: "info" },
  adjustment_requested: { word: "Adjustment asked for", tone: "warn" },
  approved: { word: "Approved", tone: "ok" },
  rejected: { word: "Rejected", tone: "crit" },
  withdrawn: { word: "Withdrawn", tone: "neutral" },
  invalidated: { word: "Replaced by a change", tone: "neutral" },
};

function stateOf(value: string): { word: string; tone: ChipTone } {
  return STATE[value] ?? { word: value.replace(/_/g, " "), tone: "neutral" };
}

/** A count over a page of a longer list: exact when the page is the list, `n+` when it is not. */
function pageCount(n: number, data: ListedRequests): string {
  return data.total > data.items.length ? `${String(n)}+` : String(n);
}

/** The record, **named by what it is** (site, carrier, period, tonnes - the server's
 * `record_label`), with the route beneath. It used to print the record's kind, so twelve rows about
 * twelve different figures read "Activity record" twelve times (G-Wcontrib-11, G-Wapprover-02). The
 * kind is still the search's and the filing link's. */
function recordCell(row: ListedRequest) {
  return (
    <EntityLink
      kind={row.record_kind === "filing_obligation" ? "filing" : "record"}
      to="/records/$recordId"
      params={{ recordId: row.record_id }}
      search={{ tab: "workflow" }}
      secondary={`${row.route_name}`}
    >
      {row.record_label}
    </EntityLink>
  );
}

/** Stages that have a **concluding** decision. `decisions` is every decision recorded, adjustment
 * requests included (ADR-0053: a reviewer's third answer leaves the stage undecided), so its length
 * is not a count of stages and printed "3 of 2 decided" once a stage had been adjusted and then
 * approved. A stage is decided by an approval or a rejection, and by one at most (the schema allows
 * one terminal decision per stage), counted by position so a repeat cannot count twice. */
function stagesDecided(row: Pick<ListedRequest, "decisions">): number {
  return new Set(
    row.decisions
      .filter((decision) => decision.decision === "approve" || decision.decision === "reject")
      .map((decision) => decision.position),
  ).size;
}

function stageWords(row: ListedRequest): string {
  if (row.state !== "pending" && row.state !== "adjustment_requested") return "";
  return row.current_position === null
    ? (row.stage_name ?? "")
    : `Stage ${String(row.current_position)} of ${String(row.stage_count)}${row.stage_name ? `, ${row.stage_name}` : ""}`;
}

/** The search box and the Outcome or State chip over a list the page holds. */
function narrow(rows: readonly ListedRequest[], filter: FilterState, key: "state" | "outcome") {
  const needle = filter.search.trim().toLowerCase();
  const picked = filter.values[key] ?? [];
  return rows.filter((row) => {
    const value = key === "state" ? row.state : (row.outcome ?? row.state);
    if (picked.length > 0 && !picked.includes(value)) return false;
    if (!needle) return true;
    return `${row.record_label} ${kindLabel(row.record_kind)} ${row.route_name} ${stateOf(value).word}`
      .toLowerCase()
      .includes(needle);
  });
}

export function SubmittedPage({ enabled }: { enabled: boolean }) {
  const read = useSubmittedByMe(enabled);
  const reader = useReader();
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTERS);
  const rows = narrow(read.data?.items ?? [], filter, "state");
  const { sorted, sort, setSort } = useGridSort(rows, {
    record: (r) => r.record_label,
    state: (r) => stateOf(r.state).word,
    when: (r) => r.submitted_at,
  });
  const columns: GridColumn<ListedRequest>[] = [
    { id: "record", header: "Record", cell: recordCell, sortable: true },
    {
      id: "state",
      header: "State",
      sortable: true,
      cell: (r) => (
        <StatusChip tone={stateOf(r.state).tone} size="sm">
          {stateOf(r.state).word}
        </StatusChip>
      ),
    },
    { id: "stage", header: "Where it is", cell: (r) => stageWords(r) || "Finished" },
    {
      id: "blocked",
      header: "Held by",
      cell: (r) =>
        r.blocked_by.length
          ? `${String(r.blocked_by.length)} open blocking ${r.blocked_by.length === 1 ? "flag" : "flags"}`
          : "Nothing",
    },
    { id: "when", header: "Submitted", sortable: true, cell: (r) => instantDate(r.submitted_at, reader) },
  ];
  const data = read.data;
  const open = data ? data.items.filter((r) => r.state === "pending" || r.state === "adjustment_requested").length : 0;
  const finished = data ? data.items.filter((r) => r.outcome !== null).length : 0;
  const stateOptions = [...new Set((data?.items ?? []).map((r) => r.state))].map((value) => ({
    value,
    label: stateOf(value).word,
  }));
  return (
    <TablePage
      icon={Hourglass}
      title="Action items"
      actions={<HowDrawer infoKey="actions.how-decisions" label="How a decision is made" />}
      label="Submitted by me"
      tiles={
        <TileStrip label="Submitted by me at a glance">
          <Tile value={data ? String(data.total) : "-"} label="Submitted" infoKey="actions.submitted" read={read} />
          <Tile value={data ? pageCount(open, data) : "-"} label="In review" read={read} />
          <Tile value={data ? pageCount(finished, data) : "-"} label="Decided" read={read} />
        </TileStrip>
      }
      filters={
        data && data.total === 0 ? undefined : (
          <FilterLine
            filters={[{ id: "state", label: "State", options: stateOptions }]}
            state={filter}
            onChange={setFilter}
            resultCount={data ? { shown: rows.length, total: data.total, noun: "requests" } : undefined}
          />
        )
      }
      table={
        read.error ? undefined : (
          <GridTable
            caption="Requests you submitted, newest first"
            columns={columns}
            rows={sorted}
            rowKey={(r) => r.id}
            loading={read.isPending}
            sort={sort}
            onSortChange={setSort}
            empty={<EmptyState icon={Hourglass} title="Nothing submitted yet" compact />}
            testId="approvals-submitted"
          />
        )
      }
      state={
        read.error ? (
          <ReadFailed of="The requests you submitted" error={read.error} onRetry={() => void read.refetch()} />
        ) : undefined
      }
    />
  );
}

export function DecidedPage({ enabled }: { enabled: boolean }) {
  const read = useDecided(enabled, false);
  const reader = useReader();
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTERS);
  const rows = narrow(read.data?.items ?? [], filter, "outcome");
  const { sorted, sort, setSort } = useGridSort(rows, {
    record: (r) => r.record_label,
    outcome: (r) => stateOf(r.outcome ?? r.state).word,
    when: (r) => r.decided_at ?? "",
  });
  const columns: GridColumn<ListedRequest>[] = [
    { id: "record", header: "Record", cell: recordCell, sortable: true },
    {
      id: "outcome",
      header: "Outcome",
      sortable: true,
      cell: (r) => {
        const outcome = stateOf(r.outcome ?? r.state);
        return (
          <StatusChip tone={outcome.tone} size="sm">
            {outcome.word}
          </StatusChip>
        );
      },
    },
    {
      id: "by",
      header: "Decided by",
      cell: (r) => (r.decided_by ? <PersonRef userId={r.decided_by} showName /> : "-"),
    },
    { id: "when", header: "Decided", sortable: true, cell: (r) => instantDate(r.decided_at, reader) },
    { id: "stages", header: "Stages", cell: (r) => `${String(stagesDecided(r))} of ${String(r.stage_count)} decided` },
  ];
  const data = read.data;
  const approved = data ? data.items.filter((r) => r.outcome === "approved").length : 0;
  const rejected = data ? data.items.filter((r) => r.outcome === "rejected").length : 0;
  return (
    <TablePage
      icon={CheckCircle}
      title="Action items"
      actions={<HowDrawer infoKey="actions.how-decisions" label="How a decision is made" />}
      label="Decided"
      tiles={
        <TileStrip label="Decided at a glance">
          <Tile value={data ? String(data.total) : "-"} label="Decided" infoKey="actions.decided" read={read} />
          <Tile value={data ? pageCount(approved, data) : "-"} label="Approved" read={read} />
          <Tile value={data ? pageCount(rejected, data) : "-"} label="Rejected" read={read} />
        </TileStrip>
      }
      filters={
        data && data.total === 0 ? undefined : (
          <FilterLine
            filters={[
              {
                id: "outcome",
                label: "Outcome",
                options: [
                  { value: "approved", label: "Approved" },
                  { value: "rejected", label: "Rejected" },
                ],
              },
            ]}
            state={filter}
            onChange={setFilter}
            resultCount={data ? { shown: rows.length, total: data.total, noun: "requests" } : undefined}
          />
        )
      }
      table={
        read.error ? undefined : (
          <GridTable
            caption="Requests approved or rejected here, latest first"
            columns={columns}
            rows={sorted}
            rowKey={(r) => r.id}
            loading={read.isPending}
            sort={sort}
            onSortChange={setSort}
            empty={<EmptyState icon={CheckCircle} title="Nothing decided yet" compact />}
            testId="approvals-decided"
          />
        )
      }
      state={
        read.error ? (
          <ReadFailed of="The decided requests" error={read.error} onRetry={() => void read.refetch()} />
        ) : undefined
      }
    />
  );
}
